<script context="module">
	import { selectedItem } from '../../../store/store.js';
	import { displayInt, tooltip, safeDivide, safeMultiply } from '../../../utils/utils.js';

	// Renamed and refactored function to return structured data
	function processRenovations(selectedItem) {
		// Extract and merge upcoming and completed renovations
		const upcomingRenovations = selectedItem.upcoming_renovation || [];
		const completedRenovations = selectedItem.completed_renovation || [];
		// Use Set to easily find unique items if needed, though filter logic handles it here
		const allRenovations = [...new Set([...upcomingRenovations, ...completedRenovations])];

		const currentYear = new Date().getFullYear();

		// Separate renovations into upcoming and completed based on the current year
		const upcomingRaw = allRenovations.filter(item => (item.renovation_year || 0) >= currentYear);
		const completedRaw = allRenovations.filter(item => (item.renovation_year || 0) < currentYear);

		// Helper function to sort and map data
		const sortAndMap = (items, isUpcoming) => {
			// Sort items by year (descending)
			items.sort((a, b) => (b.renovation_year || 0) - (a.renovation_year || 0));

			// Use a Set of upcoming renovations for efficient verification check
			const upcomingSet = new Set(upcomingRenovations);

			return items.map(item => ({
				year: item.renovation_year || "Ej angivet",
				description: item.renovation_project_name || "Beskrivning saknas",
				// Mark as not verified if it's completed but was originally in the upcoming list
				verified: isUpcoming || !upcomingSet.has(item)
			}));
		};

		return {
			upcoming: sortAndMap(upcomingRaw, true),
			completed: sortAndMap(completedRaw, false)
		};
	}
</script>

<script>
	// Reactive declaration to get processed renovations when selectedItem changes
	$: renovations = processRenovations($selectedItem);
</script>

<!-- Restructured HTML using classes from EconomyAndIncomes.svelte -->
<div class="key-value-table box">
    <div class="header-card">
        <h5><i class="fa-solid fa-wrench fa-sm icon-detail"></i> Drift & Underhåll</h5>
    </div>

    <div class="sections-container">
        <!-- Costs & Fund Section -->
        <div class="section">
            <h6 class="section-title">Driftskostnader & Fond</h6>
            <div class="metrics-grid">
                {#if $selectedItem.fund_for_exterior}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Fond för yttre underhåll</h6>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.fund_for_exterior) } kr
                    </div>
                    {#if $selectedItem.living_area_sqm}
                    <div class="metric-subvalue">
                        { safeDivide($selectedItem.fund_for_exterior, $selectedItem.living_area_sqm, "kr/m²") }
                    </div>
                    {/if}
                </div>
                {/if}
                {#if $selectedItem.energy_cost_per_sqm}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Energikostnad per kvm</h6>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.energy_cost_per_sqm) } kr/m²
                    </div>
                    {#if $selectedItem.living_area_sqm}
                    <div class="metric-subvalue">
                        { safeMultiply($selectedItem.energy_cost_per_sqm, $selectedItem.living_area_sqm, "kr") }
                    </div>
                    {/if}
                </div>
                {/if}
                {#if $selectedItem.total_operating_expenses}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Total summa driftskostnader</h6>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.total_operating_expenses) } kr
                    </div>
                    {#if $selectedItem.living_area_sqm}
                    <div class="metric-subvalue">
                        { safeDivide($selectedItem.total_operating_expenses, $selectedItem.living_area_sqm, "kr/m²") }
                    </div>
                    {/if}
                </div>
                {/if}
                {#if $selectedItem.total_maintenance_expenses}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Totala underhållskostnader</h6>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.total_maintenance_expenses) } kr
                    </div>
                    {#if $selectedItem.living_area_sqm}
                    <div class="metric-subvalue">
                        { safeDivide($selectedItem.total_maintenance_expenses, $selectedItem.living_area_sqm, "kr/m²") }
                    </div>
                    {/if}
                </div>
                {/if}
            </div>
        </div>

        <!-- Renovations Section -->
        <div class="section">
            <h6 class="section-title">Renoveringar</h6>
            <div class="renovation-list-container">
                <!-- Upcoming Renovations -->
                <div class="renovation-subsection">
                    <h6 class="subsection-title">Kommande renoveringar</h6>
                    {#if renovations.upcoming.length > 0}
                        <ul class="renovation-list">
                            {#each renovations.upcoming as item}
                                <li class="renovation-item">
                                    <span class="renovation-year">{item.year}</span>
                                    <span class="renovation-description">{item.description}</span>
                                </li>
                            {/each}
                        </ul>
                    {:else}
                        <p class="no-data">Uppgifter saknas</p>
                    {/if}
                </div>

                <!-- Completed Renovations -->
                <div class="renovation-subsection">
                    <h6 class="subsection-title">Slutförda renoveringar</h6>
                    {#if renovations.completed.length > 0}
                        <ul class="renovation-list">
                            {#each renovations.completed as item}
                                <li class="renovation-item">
                                    <span class="renovation-year">{item.year}</span>
                                    <span class="renovation-description">
                                        {item.description}
                                        {#if !item.verified}
                                            <!-- Wrap the unverified tag in a tooltip container -->
                                            <div class="tooltip-container unverified-tooltip-container" use:tooltip>
                                                <span class="unverified-tag">[Ej verifierat]</span>
                                                <div class="tooltip-content">
                                                    Denna renovering rapporterades som kommande i en tidigare årsredovisning. Även om det planerade året har passerat, har vi ännu inte kunnat bekräfta att renoveringen är slutförd genom en nyare årsredovisning.
                                                </div>
                                            </div>
                                        {/if}
                                    </span>
                                </li>
                            {/each}
                        </ul>
                    {:else}
                        <p class="no-data">Uppgifter saknas</p>
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div>

<!-- Added styles similar to EconomyAndIncomes.svelte -->
<style>

    /* Horizontal layout for sections */
    .sections-container {
        display: flex;
        gap: 24px;
        flex-wrap: wrap; /* Allow wrapping on smaller screens */
    }

    .section {
        flex: 1;
        min-width: 250px; /* Minimum width before wrapping */
    }

    .metrics-grid {
        display: grid;
        /* Adjust columns for responsiveness */
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        gap: 16px;
    }

    /* Renovation specific styles */
     .renovation-list-container {
        display: flex;
        flex-direction: column;
        gap: 20px; /* Space between upcoming and completed sections */
    }

    .subsection-title {
        font-size: 0.95rem;
        color: #495057;
        margin-bottom: 10px;
        font-weight: 500;
    }

    .renovation-list {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .renovation-item {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        border-bottom: 1px solid #e9ecef;
        font-size: 0.9rem;
    }

    .renovation-item:last-child {
        border-bottom: none;
    }

    .renovation-year {
        color: #6c757d;
        min-width: 80px; /* Ensure year alignment */
        text-align: left;
        flex-shrink: 0; /* Prevent year from shrinking */
        padding-right: 10px;
    }

    .renovation-description {
        color: #212529;
        text-align: right;
        flex-grow: 1;
        /* Allow inline-block elements like the tooltip container */
        display: inline;
    }

    .unverified-tag {
        font-style: italic;
        color: #dc3545; /* Bootstrap danger color */
        /* margin-left: 5px; Spacing handled by container */
        font-size: 0.8em;
        cursor: help; /* Indicate hover interaction */
        z-index: 1;
    }

    /* Tooltip styles should come from global CSS */
    .tooltip-container {
        position: relative; /* Still needed for absolute positioning of tooltip-content */
        display: inline-block; /* Keep it inline with text */
        margin-left: 5px; /* Add spacing previously on the tag */
        vertical-align: middle; /* Align nicely with text */
    }

    .no-data {
        font-style: italic;
        color: #6c757d;
        font-size: 0.9rem;
        padding: 8px 0;
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
        .sections-container {
            flex-direction: column;
        }
        .metrics-grid {
           grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        }
         .renovation-item {
            flex-direction: column; /* Stack year and description */
            align-items: flex-start;
            gap: 4px;
        }
        .renovation-description {
            text-align: left;
            display: block; /* Make description block for easier tooltip reading */
            width: 100%; 
        }
    }
</style>